import { FC, useMemo, useState } from 'react';
import { Prompt } from '../../shared/Prompt';
import { Button } from '../../shared/Button';
import { useUserContext } from '../../../providers/UserProvider';
import { useContentPageVersionBySlugQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-policy-versions.generated';
import { useUserProfileUpdateMutation } from '../../../libs/graphql/baseAppAPI/mutations/__generated__/user-profile-update.mutation.generated';
import { locale, xTenantId } from '../../../consts';
import { Endpoint } from '../../../models/api.model';
import useDestroyUserSession from '../../../hooks/auth/useDestroyUserSession';
import { ContentPrompt } from '../../Registration/steps/ContentPrompt';
import { Policies } from '../../Registration/types';
import { useDispatch } from 'react-redux';
import { setCashierModal } from '../../../state/cashier/reducers';
import { CashierType } from '../../../state/cashier';
import { useSnackbar } from '../../../hooks/snackbar';
import './styles.scss';

export const PolicyChecks: FC = () => {
  const { consents, userProfileId, fetchUserProfile } = useUserContext();
  const { logoutUser } = useDestroyUserSession();
  const [contentSlug, setContentSlug] = useState<string | null>(null);
  const dispatch = useDispatch();
  const { addSnack } = useSnackbar();

  const { data } = useContentPageVersionBySlugQuery({
    variables: {
      language: locale,
      slug1: 'terms-and-conditions',
      slug2: 'privacy-policy',
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  const [userProfileUpdateMutation, { loading }] = useUserProfileUpdateMutation();

  const updatedPolicies = useMemo(() => {
    if (!consents || !data) return [];

    const privacyVersion = data.privacyDoc?.[0]?.revisions?.[0]?.document_revisions_id?.version;
    const termsVersion = data.termsDoc?.[0]?.revisions?.[0]?.document_revisions_id?.version;

    const privacyVersionMismatch = privacyVersion && consents.lastPrivacyPolicyVersionAccepted !== privacyVersion;
    const termsVersionMismatch = termsVersion && consents.lastTermsAndConditionsVersionAccepted !== termsVersion;

    const updated = [];

    if (privacyVersionMismatch) updated.push({ name: 'Privacy Policy', slug: 'privacy-policy' });

    if (termsVersionMismatch) updated.push({ name: 'Terms and Conditions', slug: 'terms-and-conditions' });

    return updated;
  }, [consents, data]);

  const handleAccept = async (): Promise<void> => {
    if (!data || !userProfileId || updatedPolicies.length === 0) return;

    const privacyVersion = data.privacyDoc?.[0]?.revisions?.[0]?.document_revisions_id?.version;
    const termsVersion = data.termsDoc?.[0]?.revisions?.[0]?.document_revisions_id?.version;
    const now = new Date().toISOString();

    const hasPrivacyUpdate = updatedPolicies.some((policy) => policy.slug === 'privacy-policy');
    const hasTermsUpdate = updatedPolicies.some((policy) => policy.slug === 'terms-and-conditions');

    try {
      await userProfileUpdateMutation({
        variables: {
          userProfileId,
          xTenantId,
          userProfileUpdateInfo: {
            ...(hasPrivacyUpdate &&
              privacyVersion && {
                lastPrivacyPolicyVersionAccepted: privacyVersion,
                lastPrivacyPolicyAcceptedOnUtc: now,
              }),
            ...(hasTermsUpdate &&
              termsVersion && {
                lastTermsAndConditionsVersionAccepted: termsVersion,
                lastTermsAndConditionsAcceptedOnUtc: now,
              }),
          },
        },
      });

      await fetchUserProfile();
      addSnack({
        type: 'success',
        message: 'Consents updated!',
        theme: 'dark',
      });
    } catch (error) {
      addSnack({
        type: 'error',
        message: `Oops, something's gone wrong`,
        theme: 'dark',
      });
    }
  };

  const handleDecline = (): void => {
    logoutUser();
  };

  const handleWithdraw = (): void => {
    dispatch(setCashierModal({ isOpen: true, type: CashierType.Withdrawal }));
  };

  if (updatedPolicies.length === 0) {
    return null;
  }

  return (
    <Prompt
      show={true}
      PrimaryButton={{ disabled: loading, text: 'ACCEPT', onClick: handleAccept }}
      SecondaryButton={{ text: 'LOGOUT', onClick: handleDecline }}
      title="Policy Updates"
      cannotClose={true}
    >
      <div className="policy-checks">
        <p>The following {updatedPolicies.length > 1 ? 'policies have' : 'policy has'} changed:</p>
        <ul className="policy-checks__policy-list">
          {updatedPolicies.map((policy) => (
            <li key={policy.slug}>
              <Button
                variant="text"
                type="button"
                className="policy-checks__policy-button"
                onClick={(): void => setContentSlug(policy.slug)}
              >
                {policy.name}
              </Button>
            </li>
          ))}
        </ul>
        <p>
          These must be accepted to continue using our services. You can{' '}
          <Button variant="text" type="button" onClick={handleWithdraw}>
            withdraw your balance here
          </Button>
          .
        </p>
      </div>
      {contentSlug && (
        <ContentPrompt
          onClose={(): void => {
            setContentSlug(null);
          }}
          modalValue={contentSlug as Policies}
        />
      )}
    </Prompt>
  );
};
